import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdVisibility } from 'react-icons/md';
import '../master/MasterData.css';

interface Vendor {
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  vendorType: string;
  tin: string;
  email: string;
  phone: string;
  city: string;
  isActive: string;
}

interface BankAccount {
  bankAccountId: number;
  accountCode: string;
  bankName: string;
  branchName: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  currency: string;
  currentBalance: number;
  isActive: string;
}

interface Payment {
  paymentId: number;
  paymentNo: string;
  paymentDate: string;
  vendorCode: string;
  vendorName: string;
  invoiceNo: string;
  invoiceAmount: number;
  paymentAmount: number;
  paymentMethod: 'CASH' | 'CHECK' | 'BANK_TRANSFER' | 'ONLINE_BANKING';
  referenceNo: string;
  bankName: string;
  checkNo: string;
  checkDate: string;
  status: 'PENDING' | 'PROCESSED' | 'CLEARED' | 'CANCELLED';
  remarks: string;
}

interface PaymentModalProps {
  payment: Payment | null;
  viewMode: boolean;
  vendors: Vendor[];
  bankAccounts: BankAccount[];
  onClose: () => void;
  onSave: (payment: Payment) => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ payment, viewMode, vendors, bankAccounts, onClose, onSave }) => {
  const [formData, setFormData] = useState<Payment>(
    payment || {
      paymentId: 0,
      paymentNo: '',
      paymentDate: new Date().toISOString().split('T')[0],
      vendorCode: '',
      vendorName: '',
      invoiceNo: '',
      invoiceAmount: 0,
      paymentAmount: 0,
      paymentMethod: 'BANK_TRANSFER',
      referenceNo: '',
      bankName: '',
      checkNo: '',
      checkDate: '',
      status: 'PENDING',
      remarks: ''
    }
  );

  const handleVendorChange = (vendorCode: string) => {
    const selectedVendor = vendors.find(v => v.vendorCode === vendorCode);
    if (selectedVendor) {
      setFormData({
        ...formData,
        vendorCode: selectedVendor.vendorCode,
        vendorName: selectedVendor.vendorName
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewMode) {
      onSave(formData);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{viewMode ? 'View Payment' : payment ? 'Edit Payment' : 'Record New Payment'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Payment No *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.paymentNo}
                  onChange={(e) => setFormData({ ...formData, paymentNo: e.target.value })}
                  placeholder="PAY-2025-XXXX"
                />
              </div>
              <div className="form-group">
                <label>Payment Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  required
                  disabled={viewMode}
                  value={formData.paymentDate}
                  onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  disabled={viewMode}
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <option value="PENDING">Pending</option>
                  <option value="PROCESSED">Processed</option>
                  <option value="CLEARED">Cleared</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Vendor Name *</label>
                <select
                  required
                  disabled={viewMode}
                  value={formData.vendorCode}
                  onChange={(e) => handleVendorChange(e.target.value)}
                >
                  <option value="">Select Vendor</option>
                  {vendors.filter(v => v.isActive === 'Y').map(vendor => (
                    <option key={vendor.vendorId} value={vendor.vendorCode}>
                      {vendor.vendorName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Invoice No *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.invoiceNo}
                  onChange={(e) => setFormData({ ...formData, invoiceNo: e.target.value })}
                  placeholder="PI-2025-XXXX"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Invoice Amount *</label>
                <input
                  type="number"
                  required
                  disabled={viewMode}
                  value={formData.invoiceAmount}
                  onChange={(e) => setFormData({ ...formData, invoiceAmount: parseFloat(e.target.value) })}
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label>Payment Amount *</label>
                <input
                  type="number"
                  required
                  disabled={viewMode}
                  value={formData.paymentAmount}
                  onChange={(e) => setFormData({ ...formData, paymentAmount: parseFloat(e.target.value) })}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="form-section">
              <h4>Payment Details</h4>

              <div className="form-row">
                <div className="form-group">
                  <label>Payment Method *</label>
                  <select
                    required
                    disabled={viewMode}
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value as any })}
                  >
                    <option value="CASH">Cash</option>
                    <option value="CHECK">Check</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="ONLINE_BANKING">Online Banking</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Reference No</label>
                  <input
                    type="text"
                    disabled={viewMode}
                    value={formData.referenceNo}
                    onChange={(e) => setFormData({ ...formData, referenceNo: e.target.value })}
                    placeholder="Transaction/Authorization No"
                  />
                </div>
              </div>

              {(formData.paymentMethod === 'CHECK' || formData.paymentMethod === 'BANK_TRANSFER' || formData.paymentMethod === 'ONLINE_BANKING') && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Bank Name {formData.paymentMethod === 'CHECK' ? '*' : ''}</label>
                    <select
                      required={formData.paymentMethod === 'CHECK'}
                      disabled={viewMode}
                      value={formData.bankName}
                      onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                    >
                      <option value="">Select Bank</option>
                      {bankAccounts
                        .filter((account, index, self) =>
                          account.isActive === 'Y' &&
                          index === self.findIndex(a => a.bankName === account.bankName)
                        )
                        .map((account, index) => (
                          <option key={index} value={account.bankName}>
                            {account.bankName}
                          </option>
                        ))
                      }
                    </select>
                  </div>
                  {formData.paymentMethod === 'CHECK' && (
                    <div className="form-group">
                      <label>Check No *</label>
                      <input
                        type="text"
                        required
                        disabled={viewMode}
                        value={formData.checkNo}
                        onChange={(e) => setFormData({ ...formData, checkNo: e.target.value })}
                      />
                    </div>
                  )}
                </div>
              )}

              {formData.paymentMethod === 'CHECK' && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Check Date *</label>
                    <input
                      type="date"
                      lang="en-US"
                      required
                      disabled={viewMode}
                      value={formData.checkDate}
                      onChange={(e) => setFormData({ ...formData, checkDate: e.target.value })}
                    />
                  </div>
                  <div className="form-group"></div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Remarks</label>
              <textarea
                disabled={viewMode}
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                rows={3}
                placeholder="Additional notes about this payment..."
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {viewMode ? 'Close' : 'Cancel'}
            </button>
            {!viewMode && (
              <button type="submit" className="btn btn-primary">
                {payment ? 'Update' : 'Record'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const Payments: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [viewMode, setViewMode] = useState(false);

  useEffect(() => {
    // TODO: Fetch bank accounts from API
    setBankAccounts([
      {
        bankAccountId: 1,
        accountCode: 'BA001',
        bankName: 'Banco de Oro (BDO)',
        branchName: 'Makati Business District',
        accountNumber: '0012-3456-7890',
        accountName: 'DEWFOAM Furniture Corporation',
        accountType: 'CHECKING',
        currency: 'PHP',
        currentBalance: 3500000.00,
        isActive: 'Y'
      },
      {
        bankAccountId: 2,
        accountCode: 'BA002',
        bankName: 'Bank of the Philippine Islands (BPI)',
        branchName: 'Ortigas Center',
        accountNumber: '0023-4567-8901',
        accountName: 'DEWFOAM Furniture Corporation',
        accountType: 'SAVINGS',
        currency: 'PHP',
        currentBalance: 1200000.00,
        isActive: 'Y'
      },
      {
        bankAccountId: 3,
        accountCode: 'BA003',
        bankName: 'Metropolitan Bank (Metrobank)',
        branchName: 'BGC Branch',
        accountNumber: '0034-5678-9012',
        accountName: 'DEWFOAM Furniture Corporation',
        accountType: 'CHECKING',
        currency: 'USD',
        currentBalance: 25000.00,
        isActive: 'Y'
      },
      {
        bankAccountId: 4,
        accountCode: 'BA004',
        bankName: 'Banco de Oro (BDO)',
        branchName: 'Manila Branch',
        accountNumber: '0045-6789-0123',
        accountName: 'DEWFOAM Furniture Payroll Account',
        accountType: 'SAVINGS',
        currency: 'PHP',
        currentBalance: 850000.00,
        isActive: 'Y'
      }
    ]);

    // TODO: Fetch vendors from API
    setVendors([
      {
        vendorId: 1,
        vendorCode: 'VEND001',
        vendorName: 'Foam Suppliers Inc.',
        vendorType: 'RAW_MATERIALS',
        tin: '123-456-789-000',
        email: 'foam@suppliers.ph',
        phone: '+63 2 1234 5678',
        city: 'Manila',
        isActive: 'Y'
      },
      {
        vendorId: 2,
        vendorCode: 'VEND002',
        vendorName: 'Chemical Trading Co.',
        vendorType: 'RAW_MATERIALS',
        tin: '987-654-321-000',
        email: 'chemical@trading.ph',
        phone: '+63 2 9876 5432',
        city: 'Quezon City',
        isActive: 'Y'
      },
      {
        vendorId: 3,
        vendorCode: 'VEND003',
        vendorName: 'Packaging Materials Ltd.',
        vendorType: 'PACKAGING',
        tin: '456-789-123-000',
        email: 'packaging@materials.ph',
        phone: '+63 2 4567 8901',
        city: 'Makati',
        isActive: 'Y'
      }
    ]);

    // TODO: Fetch payments from API
    setPayments([
      {
        paymentId: 1,
        paymentNo: 'PAY-2025-0001',
        paymentDate: '2025-02-28',
        vendorCode: 'VEND001',
        vendorName: 'Foam Suppliers Inc.',
        invoiceNo: 'PI-2025-0001',
        invoiceAmount: 1000000,
        paymentAmount: 500000,
        paymentMethod: 'BANK_TRANSFER',
        referenceNo: 'TRF-98765',
        bankName: 'BDO',
        checkNo: '',
        checkDate: '',
        status: 'CLEARED',
        remarks: 'Partial payment - 50%'
      },
      {
        paymentId: 2,
        paymentNo: 'PAY-2025-0002',
        paymentDate: '2025-03-01',
        vendorCode: 'VEND002',
        vendorName: 'Chemical Trading Co.',
        invoiceNo: 'PI-2025-0002',
        invoiceAmount: 500000,
        paymentAmount: 500000,
        paymentMethod: 'CHECK',
        referenceNo: '',
        bankName: 'BPI',
        checkNo: 'CHK-456789',
        checkDate: '2025-03-01',
        status: 'PROCESSED',
        remarks: 'Full payment via check'
      },
      {
        paymentId: 3,
        paymentNo: 'PAY-2025-0003',
        paymentDate: '2025-03-05',
        vendorCode: 'VEND003',
        vendorName: 'Packaging Materials Ltd.',
        invoiceNo: 'PI-2025-0003',
        invoiceAmount: 300000,
        paymentAmount: 300000,
        paymentMethod: 'ONLINE_BANKING',
        referenceNo: 'ONL-12345',
        bankName: 'Metrobank',
        checkNo: '',
        checkDate: '',
        status: 'PENDING',
        remarks: 'Awaiting confirmation'
      }
    ]);
  }, []);

  const filteredPayments = payments.filter(payment =>
    payment.paymentNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.vendorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingPayment(null);
    setViewMode(false);
    setShowModal(true);
  };

  const handleEdit = (payment: Payment) => {
    setEditingPayment(payment);
    setViewMode(false);
    setShowModal(true);
  };

  const handleView = (payment: Payment) => {
    setEditingPayment(payment);
    setViewMode(true);
    setShowModal(true);
  };

  const handleDelete = (paymentId: number) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      setPayments(payments.filter(p => p.paymentId !== paymentId));
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'CLEARED': return 'badge-success';
      case 'PROCESSED': return 'badge-info';
      case 'PENDING': return 'badge-warning';
      case 'CANCELLED': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  const getPaymentMethodBadge = (method: string) => {
    switch (method) {
      case 'CASH': return 'badge-success';
      case 'CHECK': return 'badge-info';
      case 'BANK_TRANSFER': return 'badge-primary';
      case 'ONLINE_BANKING': return 'badge-blue';
      default: return 'badge-secondary';
    }
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Payments</h2>
            <p className="subtitle">Record vendor payments and manage payables</p>
          </div>
          <button className="btn btn-primary" onClick={handleAdd}>
            <MdAdd /> Record New Payment
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search payments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Payment No</th>
                  <th>Date</th>
                  <th>Vendor</th>
                  <th>Invoice No</th>
                  <th>Invoice Amt</th>
                  <th>Payment Amt</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map(payment => (
                  <tr key={payment.paymentId}>
                    <td className="code">{payment.paymentNo}</td>
                    <td>{new Date(payment.paymentDate).toLocaleDateString('en-US')}</td>
                    <td>{payment.vendorName}</td>
                    <td>{payment.invoiceNo}</td>
                    <td className="amount">₱{payment.invoiceAmount.toLocaleString()}</td>
                    <td className="amount">₱{payment.paymentAmount.toLocaleString()}</td>
                    <td>
                      <span className={`badge ${getPaymentMethodBadge(payment.paymentMethod)}`}>
                        {payment.paymentMethod.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(payment.status)}`}>
                        {payment.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleView(payment)}
                          title="View"
                        >
                          <MdVisibility />
                        </button>
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(payment)}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDelete(payment.paymentId)}
                          title="Delete"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total Paid:</td>
                  <td colSpan={4} style={{ fontWeight: 'bold' }}>
                    ₱{payments.reduce((sum, p) => sum + p.paymentAmount, 0).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>

            {filteredPayments.length === 0 && (
              <div className="no-data">
                <p>No payments found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <PaymentModal
          payment={editingPayment}
          viewMode={viewMode}
          vendors={vendors}
          bankAccounts={bankAccounts}
          onClose={() => setShowModal(false)}
          onSave={(payment) => {
            if (editingPayment) {
              setPayments(payments.map(p =>
                p.paymentId === payment.paymentId ? payment : p
              ));
            } else {
              setPayments([...payments, { ...payment, paymentId: Date.now() }]);
            }
            setShowModal(false);
          }}
        />
      )}
    </MainLayout>
  );
};

export default Payments;
